import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Package, Clock } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { useSettings } from '@/hooks/useProducts';
import api from '@/lib/api';
import DocumentHead from '@/components/DocumentHead';
import BrandSymbol from '@/components/BrandSymbol';

export default function Bundles() {
  const { t } = useTranslation('bundles');
  const { data, isLoading } = useQuery({
    queryKey: ['bundles'],
    queryFn: () =>
      api.get<{ bundles: any[] }>('/bundles'),
  });
  const { data: settingsData } = useSettings();

  const bundlesTitle = settingsData?.settings?.bundles_title || t('documentHead.title');
  const bundlesDescription = settingsData?.settings?.bundles_description || t('documentHead.description');
  const bundles = data?.bundles || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <DocumentHead
        title={bundlesTitle}
        description={bundlesDescription}
        canonicalUrl="/bundles"
      />
      <div className="text-center mb-8">
        <h1 className="text-3xl font-display font-bold text-stone-900">{bundlesTitle}</h1>
        <p className="mt-2 text-stone-600">{bundlesDescription}</p>
      </div>

      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse rounded-xl border border-stone-200 bg-white p-6">
              <div className="h-40 bg-stone-200 rounded-lg mb-4" />
              <div className="h-5 bg-stone-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-stone-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : bundles.length === 0 ? (
        <div className="text-center py-16">
          <Package className="h-16 w-16 text-stone-300 mx-auto mb-4" aria-hidden="true" />
          <p className="text-stone-500">{t('list.empty')}</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {bundles.map((bundle: any) => {
            const items = bundle.items || [];
            const totalPrice = items.reduce((sum: number, i: any) => sum + Number(i.price) * (i.quantity || 1), 0);
            let discountedPrice = totalPrice;
            if (bundle.discount_type === 'percentage') {
              discountedPrice = totalPrice * (1 - Number(bundle.discount_value) / 100);
            } else if (bundle.discount_type === 'fixed_amount') {
              discountedPrice = Math.max(0, totalPrice - Number(bundle.discount_value));
            }

            return (
              <Link
                key={bundle.id}
                to={`/bundles/${bundle.slug}`}
                className="group rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden transition-shadow hover:shadow-md"
              >
                <div className="aspect-[16/9] overflow-hidden bg-stone-100">
                  {bundle.image_url ? (
                    <img
                      src={bundle.image_url}
                      alt={bundle.name}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center gap-2">
                      {items.slice(0, 3).map((item: any, i: number) => (
                        item.image_url ? (
                          <img key={i} src={item.image_url} alt="" className="h-20 w-20 object-cover rounded-lg" />
                        ) : (
                          <div key={i} className="h-20 w-20 bg-stone-200 rounded-lg flex items-center justify-center">
                            <BrandSymbol className="h-6 w-6 text-stone-400" />
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h2 className="text-lg font-bold text-stone-900 group-hover:text-sage-700 transition-colors">
                    {bundle.name}
                  </h2>
                  {bundle.description && (
                    <p className="mt-1 text-sm text-stone-500 line-clamp-2">{bundle.description}</p>
                  )}
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-xl font-bold text-sage-700">{formatCurrency(discountedPrice)}</span>
                    {discountedPrice < totalPrice && (
                      <span className="text-sm text-stone-400 line-through">{formatCurrency(totalPrice)}</span>
                    )}
                  </div>
                  <p className="text-xs text-stone-500 mt-1">
                    {t('list.itemsIncluded', { count: items.length })}
                  </p>
                  {(bundle.ends_at || bundle.stock_limit != null) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {bundle.ends_at && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                          <Clock className="h-3 w-3" aria-hidden="true" />
                          {t('list.limitedTime')}
                        </span>
                      )}
                      {bundle.stock_limit != null && (
                        <span className="text-[11px] font-medium text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded">
                          {t('list.remaining', { count: Math.max(0, bundle.stock_limit - (bundle.stock_sold || 0)) })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
